import { AddressService } from '@/common/services/address/address.service';
import { ValidationService } from '@/common/services/validation/validation.service';
import { splitPhoneLada } from '@/common/utils/strings/phone.utils';
import { isValid } from '@/common/utils/validation/common.utils';
import { CitiesResponse } from '@/core/domain/address/models/cities-response.model';
import {
    CountriesResponse,
    Country,
} from '@/core/domain/address/models/countries-response.model';
import {
    Neighborhood,
    NeighborhoodsResponse,
} from '@/core/domain/address/models/neighborhoods-response.model';
import { birthdayValidator } from '@/common/utils/validation/datesValidators.utils';
import {
    State,
    StatesResponse,
} from '@/core/domain/address/models/states-response.model';
import { GetCitiesUseCase } from '@/core/domain/address/usecases/getCities.usecase';
import { GetCountriesUseCase } from '@/core/domain/address/usecases/getCountries.usecase';
import { GetNeighborhoodsUseCase } from '@/core/domain/address/usecases/getNeighborhoods.usecase';
import { GetStatesUseCase } from '@/core/domain/address/usecases/getSates.usecase';
import {
    UserAdditionalDriver,
    UserRenterDetails,
} from '@/core/domain/user/models/user-response.model';
import { GetUserUseCase } from '@/core/domain/user/usecases/getUser.usecase copy';
import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import _ from 'lodash';
import { Observable, of, switchMap } from 'rxjs';

@Component({
    selector: 'fe-card-general-information',
    templateUrl: './card-general-information.component.html',
    styleUrls: ['./card-general-information.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CardGeneralInformationComponent {
    public formActiveReservation!: FormGroup;

    public loadingCountries = false;
    public loadingStates = false;
    public loadingCities = false;
    public loadingNeighborhoods = false;

    public disabledZipCode = false;

    public additionalDriver = false;

    public renterDetails!: UserRenterDetails | undefined;
    public additionalDetails!: UserAdditionalDriver | undefined;

    constructor(
        public addressService: AddressService,
        private readonly getCountriesUseCase: GetCountriesUseCase,
        private readonly getStatesUseCase: GetStatesUseCase,
        private readonly getCitiesUseCase: GetCitiesUseCase,
        private readonly getNeighborhoodsUseCase: GetNeighborhoodsUseCase,
        private readonly formBuilder: FormBuilder,
        private readonly validation: ValidationService,
        private readonly getUserCase: GetUserUseCase,
        @Inject('INPUT_DATEPICKER') public inputDatePickerFormat: string,
    ) {
        this.initData();
        this.initFormActiveReservation();
    }

    public getForm(): FormGroup {
        return this.formActiveReservation;
    }

    private fillForm(): void {
        this.formActiveReservation
            .get('name')
            ?.setValue(this.renterDetails?.name);
        this.formActiveReservation
            .get('lastName')
            ?.setValue(this.renterDetails?.lastName);
        this.formActiveReservation
            .get('email')
            ?.setValue(this.renterDetails?.email);
        const phone = splitPhoneLada(this.renterDetails?.phone);
        this.formActiveReservation.get('lada')?.setValue(phone.lada);
        this.formActiveReservation.get('phone')?.setValue(phone.number);
        this.formActiveReservation
            .get('street')
            ?.setValue(this.renterDetails?.street);
        this.formActiveReservation
            .get('outdoorNumber')
            ?.setValue(this.renterDetails?.buildingNumber);
        this.formActiveReservation
            .get('zipCode')
            ?.setValue(this.renterDetails?.zipCode);

        if (this.additionalDriver) {
            this.formActiveReservation
                .get('name2')
                ?.setValue(this.additionalDetails?.name);
            this.formActiveReservation
                .get('lastName2')
                ?.setValue(this.additionalDetails?.lastName);
            this.formActiveReservation
                .get('email2')
                ?.setValue(this.additionalDetails?.email);

            this.formActiveReservation
                .get('phone2')
                ?.setValue(splitPhoneLada(this.additionalDetails?.phone).number);
        }
    }

    private initFormActiveReservation(): void {
        this.formActiveReservation = this.formBuilder.group({
            name: ['', Validators.compose([Validators.required])],
            lastName: ['', Validators.compose([Validators.required])],
            email: [
                '',
                Validators.compose([
                    Validators.required,
                    this.validation.validEmail,
                ]),
            ],
            lada: [''],
            phone: ['', Validators.compose([Validators.required])],
            birthday: [
                '',
                Validators.compose([Validators.required, birthdayValidator()]),
            ],
            street: ['', Validators.compose([Validators.required])],
            outdoorNumber: ['', Validators.compose([Validators.required])],
            country: ['', Validators.compose([Validators.required])],
            state: ['', Validators.compose([Validators.required])],
            city: ['', Validators.compose([Validators.required])],
            neighborhood: ['', Validators.compose([Validators.required])],
            zipCode: ['', Validators.compose([Validators.required])],
        });

        this.disabledZipCode = true;
    }

    private additionalDriverForm(): void {
        if (this.additionalDriver) {
            this.formActiveReservation.addControl(
                'name2',
                this.formBuilder.control(
                    '',
                    Validators.compose([Validators.required]),
                ),
            );
            this.formActiveReservation.addControl(
                'lastName2',
                this.formBuilder.control(
                    '',
                    Validators.compose([Validators.required]),
                ),
            );
            this.formActiveReservation.addControl(
                'email2',
                this.formBuilder.control(
                    '',
                    Validators.compose([
                        Validators.required,
                        this.validation.validEmail,
                    ]),
                ),
            );
            this.formActiveReservation.addControl(
                'phone2',
                this.formBuilder.control(
                    '',
                    Validators.compose([Validators.required]),
                ),
            );
        }
    }

    private initData(): void {
        this.getUserCase
            .execute()
            .pipe(
                switchMap((response) => {
                    this.renterDetails = response.data?.renterDetails;
                    this.additionalDetails = response.data?.additionalDriver;

                    if (response.data?.hasAdditionalDriver === true) {
                        this.additionalDriver = true;
                        this.additionalDriverForm();
                    }

                    this.fillForm();
                    this.initCountries();
                    return of(response);
                }),
            )
            .subscribe();
    }

    private initCountries(): void {
        this.loadingCountries = true;
        this.getCountriesUseCase
            .execute()
            .pipe(
                switchMap((response) => this.fillCountry(response)),
                switchMap((response) => this.fillState(response)),
                switchMap((response) => this.fillCity(response)),
                switchMap((response) => this.fillNeighborhood(response)),
            )
            .subscribe();
    }

    private fillNeighborhood(response: NeighborhoodsResponse) {
        const neighborhoodId = _.find(
            response?.data?.suburbs,
            (item: Neighborhood) => item.id === this.renterDetails?.suburbId,
        )?.id;
        this.formActiveReservation
            .get('neighborhood')
            ?.setValue([neighborhoodId]);
        this.loadingNeighborhoods = false;
        return of(null);
    }

    private fillCity(
        response: CitiesResponse,
    ): Observable<NeighborhoodsResponse> {
        const cityId = _.find(
            response?.data?.cities,
            (item: State) => item.id === this.renterDetails?.cityId,
        )?.id;
        this.formActiveReservation.get('city')?.setValue([cityId]);
        this.loadingCities = false;
        this.loadingNeighborhoods = true;
        return this.getNeighborhoodsUseCase.execute(cityId as number);
    }

    private fillState(response: StatesResponse): Observable<CitiesResponse> {
        const stateId = _.find(
            response?.data?.states,
            (item: State) => item.id === this.renterDetails?.stateId,
        )?.id;
        this.formActiveReservation.get('state')?.setValue([stateId]);
        this.loadingStates = false;
        this.loadingCities = true;
        return this.getCitiesUseCase.execute(stateId as number);
    }

    private fillCountry(
        response: CountriesResponse,
    ): Observable<StatesResponse> {
        const countryId = _.find(
            response?.data?.countries,
            (item: Country) => item.id === this.renterDetails?.countryId,
        )?.id;
        if (countryId) {
            this.formActiveReservation.get('country')?.setValue([countryId]);
            this.loadingCountries = false;
            this.loadingStates = true;
        }
        return this.getStatesUseCase.execute(countryId as number);
    }

    public onChangeCountry(): void {
        const countryId = _.first(
            this.formActiveReservation.get('country')?.value,
        ) as number;

        this.addressService.states = [];
        this.addressService.cities = [];
        this.addressService.neighborhoods = [];
        this.addressService.zipCodes = [];
        this.formActiveReservation.get('state')?.setValue(null);
        this.formActiveReservation.get('city')?.setValue(null);
        this.formActiveReservation.get('neighborhood')?.setValue(null);
        this.formActiveReservation.get('zipCode')?.setValue(null);
        this.disabledZipCode = false;

        if (!isValid(countryId)) {
            return;
        }

        this.getStates(countryId);
    }

    private getStates(countryId: number) {
        this.loadingStates = true;
        this.getStatesUseCase
            .execute(countryId)
            .subscribe(() => (this.loadingStates = false));
    }

    public onChangeState(): void {
        const stateId = _.first(
            this.formActiveReservation.get('state')?.value,
        ) as number;

        this.addressService.cities = [];
        this.addressService.neighborhoods = [];
        this.addressService.zipCodes = [];
        this.formActiveReservation.get('city')?.setValue(null);
        this.formActiveReservation.get('neighborhood')?.setValue(null);
        this.formActiveReservation.get('zipCode')?.setValue(null);
        this.disabledZipCode = false;

        if (!isValid(stateId)) {
            return;
        }

        this.getCities(stateId);
    }

    private getCities(stateId: number): void {
        this.loadingCities = true;
        this.getCitiesUseCase
            .execute(stateId)
            .subscribe(() => (this.loadingCities = false));
    }

    public onChangeCity(): void {
        const cityId = _.first(
            this.formActiveReservation.get('city')?.value,
        ) as number;

        this.addressService.neighborhoods = [];
        this.addressService.zipCodes = [];
        this.formActiveReservation.get('neighborhood')?.setValue(null);
        this.formActiveReservation.get('zipCode')?.setValue(null);
        this.disabledZipCode = false;

        if (!isValid(cityId)) {
            return;
        }

        this.getNeighborhoods(cityId);
    }

    private getNeighborhoods(cityId: number): void {
        this.loadingNeighborhoods = true;
        this.getNeighborhoodsUseCase
            .execute(cityId)
            .subscribe(() => (this.loadingNeighborhoods = false));
    }

    public onChangeNeighborhood(): void {
        const neighborhoodId = _.first(
            this.formActiveReservation.get('neighborhood')?.value,
        ) as number;

        if (!isValid(neighborhoodId)) {
            this.disabledZipCode = false;
            this.addressService.zipCode = null;
            this.formActiveReservation.get('zipCode')?.setValue(null);
            return;
        }

        this.disabledZipCode = true;

        this.formActiveReservation
            .get('zipCode')
            ?.setValue(this.addressService.getZipCode(neighborhoodId));
    }
}
